import { useEffect, useState } from "react";

// Mensagens "enviando": entram na conversa no instante do envio, antes de o servidor
// responder, e saem quando a mensagem de verdade chega (resposta do POST ou socket).
// Ficam fora do React porque quem envia (barra de digitação) e quem mostra (lista) são
// componentes irmãos; vivem só na memória desta aba.

let seq = 0;
let entries = [];
const listeners = new Set();
const realListeners = new Set();
const staleListeners = new Set();
const staleTimers = new Map();
const previews = new Map(); // id da mensagem real -> prévia local (evita a imagem piscar na troca)

const STALE_AFTER = { text: 10000, media: 30000 };

const notify = () => listeners.forEach((fn) => fn());
const sameTicket = (a, b) => a != null && b != null && String(a) === String(b);

export const subscribePending = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

// a lista é avisada quando o POST já devolve a mensagem gravada
export const onPendingReal = (fn) => {
  realListeners.add(fn);
  return () => realListeners.delete(fn);
};

// ...e quando o envio deu certo mas a mensagem não apareceu (socket caído): hora de recarregar
export const onPendingStale = (fn) => {
  staleListeners.add(fn);
  return () => staleListeners.delete(fn);
};

export const getPending = (ticketId) => entries.filter((e) => sameTicket(e.ticketId, ticketId));

export const hasPending = (localId) => entries.some((e) => e.localId === localId);

export const addPending = (ticketId, data) => {
  seq += 1;
  const entry = {
    localId: `${Date.now()}-${seq}`,
    ticketId,
    status: "sending", // sending | sent (servidor aceitou, falta a mensagem real) | error
    progress: null,
    errorText: null,
    createdAt: new Date().toISOString(),
    ...data,
  };
  entries = [...entries, entry];
  notify();
  return entry.localId;
};

export const updatePending = (localId, patch) => {
  let changed = false;
  entries = entries.map((e) => {
    if (e.localId !== localId) return e;
    changed = true;
    return { ...e, ...patch };
  });
  if (changed) notify();
};

const revokeLater = (url) => {
  if (!url) return;
  // a bolha definitiva usa a prévia até a imagem do servidor carregar
  setTimeout(() => {
    try {
      window.URL.revokeObjectURL(url);
    } catch (e) {
      /* já liberada */
    }
  }, 120000);
};

export const removePending = (localId) => {
  const entry = entries.find((e) => e.localId === localId);
  if (!entry) return;
  clearTimeout(staleTimers.get(localId));
  staleTimers.delete(localId);
  entries = entries.filter((e) => e.localId !== localId);
  revokeLater(entry.previewUrl);
  notify();
};

export const getPreviewFor = (messageId) => previews.get(messageId) || null;

const rememberPreview = (message, entry) => {
  if (!entry.previewUrl || entry.mediaKind !== "image" || message.id == null) return;
  previews.set(message.id, entry.previewUrl);
  if (previews.size > 40) previews.delete(previews.keys().next().value);
};

const normalize = (text) =>
  String(text == null ? "" : text)
    .replace(/[​-‍﻿]/g, "")
    .trim();

const kindOfMessage = (message) => (message.mediaUrl ? "media" : "text");

export const mediaKindOf = (message) => {
  const type = String(message.mediaType || "");
  if (type === "image") return "image";
  if (type === "video") return "video";
  if (["audio", "audioMessage", "ptt"].includes(type)) return "audio";
  return "file";
};

// Qual "enviando" esta mensagem real substitui? Primeiro o de mesmo conteúdo; se o servidor
// mexeu no texto (variáveis, assinatura), o mais antigo do mesmo tipo.
export const findPendingMatch = (candidates, message) => {
  if (!message || !message.fromMe || candidates.length === 0) return null;
  const kind = kindOfMessage(message);
  const sameKind = candidates.filter((e) => e.kind === kind && !!e.isPrivate === !!message.isPrivate);
  if (sameKind.length === 0) return null;
  if (kind === "text") {
    const body = normalize(message.body);
    return sameKind.find((e) => normalize(e.body) === body) || sameKind[0];
  }
  const mediaKind = mediaKindOf(message);
  return sameKind.find((e) => e.mediaKind === mediaKind) || sameKind[0];
};

// A mesma mensagem real chega duas vezes quando o servidor a devolve no POST e também a emite
// pelo socket: a segunda não pode levar embora outro "enviando" da fila.
const matchedRealIds = new Set();

// Mensagem real nova (socket ou resposta do POST): tira o "enviando" correspondente.
export const matchRealMessage = (ticketId, message) => {
  if (!message || message.id == null || matchedRealIds.has(message.id)) return null;
  const entry = findPendingMatch(getPending(ticketId), message);
  if (!entry) return null;
  matchedRealIds.add(message.id);
  if (matchedRealIds.size > 200) matchedRealIds.delete(matchedRealIds.values().next().value);
  rememberPreview(message, entry);
  removePending(entry.localId);
  return entry;
};

// A conversa foi (re)carregada: "enviando" já aceito pelo servidor cuja mensagem está na
// lista sai, sem esperar o socket. Só casa por conteúdo e com mensagem recente.
export const reconcilePending = (ticketId, messages) => {
  if (!Array.isArray(messages)) return;
  const used = new Set();
  getPending(ticketId)
    .filter((e) => e.status === "sent")
    .forEach((entry) => {
      const since = new Date(entry.createdAt).getTime() - 120000;
      const real = messages.find((m) => {
        if (!m.fromMe || used.has(m.id) || new Date(m.createdAt).getTime() < since) return false;
        if (kindOfMessage(m) !== entry.kind || !!m.isPrivate !== !!entry.isPrivate) return false;
        return entry.kind === "text" ? normalize(m.body) === normalize(entry.body) : mediaKindOf(m) === entry.mediaKind;
      });
      if (!real) return;
      used.add(real.id);
      matchedRealIds.add(real.id);
      rememberPreview(real, entry);
      removePending(entry.localId);
    });
};

// O que o POST devolveu, se devolveu a(s) mensagem(ns) gravada(s).
const extractMessages = (data) => {
  const list = [];
  const visit = (value) => {
    if (!value || typeof value !== "object") return;
    if (Array.isArray(value)) return value.forEach(visit);
    if (value.id != null && (value.wid !== undefined || value.ticketId !== undefined) && "body" in value) {
      list.push(value);
      return;
    }
    if (value.message) visit(value.message);
    if (value.messages) visit(value.messages);
  };
  visit(data);
  return list;
};

export const markPendingSent = (localIds, data) => {
  const live = entries.filter((e) => localIds.includes(e.localId));
  if (live.length === 0) return;
  const ticketId = live[0].ticketId;
  extractMessages(data).forEach((message) => realListeners.forEach((fn) => fn(ticketId, message)));
  live.forEach((entry) => {
    if (!hasPending(entry.localId)) return; // já foi trocado pela mensagem real
    updatePending(entry.localId, { status: "sent", progress: 100 });
    clearTimeout(staleTimers.get(entry.localId));
    staleTimers.set(
      entry.localId,
      setTimeout(() => {
        staleTimers.delete(entry.localId);
        if (!hasPending(entry.localId)) return;
        staleListeners.forEach((fn) => fn(ticketId));
        // dá tempo de a lista recarregar e casar; depois disso o "enviando" não fica preso
        setTimeout(() => removePending(entry.localId), 4000);
      }, STALE_AFTER[entry.kind] || STALE_AFTER.text)
    );
  });
};

export const markPendingError = (localIds, errorText) => {
  localIds.forEach((localId) => updatePending(localId, { status: "error", progress: null, errorText: errorText || null }));
};

export const usePendingMessages = (ticketId) => {
  const [list, setList] = useState(() => getPending(ticketId));
  useEffect(() => {
    const sync = () => setList(getPending(ticketId));
    sync();
    return subscribePending(sync);
  }, [ticketId]);
  return list;
};
